import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Zap, Sparkles, Gift, ArrowRight, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { api, formatPrice, formatApiErrorDetail } from "@/lib/api";

// "Quick Buys" — low-friction entry offers.
// 1. Try a bootcamp class free → prefills consult form
// 2. Bootcamp drop-in (one-time Square checkout)
// 3. 3-Day PT trial (one-time Square checkout)
export default function QuickBuys({ onConsult, onSelectPlan }) {
  const [packages, setPackages] = useState([]);
  const [loadingId, setLoadingId] = useState(null);

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  const dropIn = packages.find((p) => p.id === "pkg-bootcamp-dropin");
  const trial = packages.find((p) => p.id === "pkg-pt-trial");

  const buyDirect = async (pkg) => {
    setLoadingId(pkg.id);
    try {
      const term = pkg.terms?.[0];
      const { data } = await api.post("/checkout", {
        package_id: pkg.id,
        months: term?.months || 1,
      });
      window.location.href = data.url;
    } catch (e) {
      toast(formatApiErrorDetail(e.response?.data?.detail), {
        description: "Let's set up your booking directly with the coach.",
      });
      onConsult?.();
    } finally {
      setLoadingId(null);
    }
  };

  const tryFree = () => {
    onSelectPlan?.({
      program: "bootcamp",
      note: "FIRST CLASS FREE — I'd like to try a complimentary bootcamp class. Please let me know availability.",
    });
    onConsult?.();
  };

  if (packages.length === 0) return null;

  return (
    <section
      id="quickbuys"
      className="relative py-24 sm:py-32 bg-[#050505] border-t border-white/5"
      data-testid="quickbuys-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <Reveal>
          <div className="mb-14 flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="font-mono-accent text-xs text-[#005EEA] tracking-[0.3em] mb-3">
                — 05 · START HERE
              </div>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.9] uppercase">
                Not ready for a plan?
                <br />
                <span className="text-[#005EEA]">Try it first.</span>
              </h2>
            </div>
            <p className="text-white/60 text-sm sm:text-base max-w-md">
              Three low-commitment ways to experience the coaching before you commit long-term.
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
          {/* CARD 1 — Free first class */}
          <Reveal delay={0.02}>
            <article
              className="relative border-2 border-[#005EEA] bg-[#005EEA]/[0.08] p-7 md:p-8 h-full flex flex-col shadow-[0_0_40px_rgba(0,94,234,0.15)]"
              data-testid="quickbuy-free"
            >
              <span className="absolute top-0 right-0 bg-[#005EEA] text-white font-mono-accent text-[10px] px-4 py-2">
                FREE
              </span>
              <Gift className="text-[#005EEA] mb-4" size={26} />
              <p className="font-mono-accent text-[10px] text-[#005EEA] mb-2">FIRST CLASS FREE</p>
              <h3 className="font-display text-2xl md:text-3xl leading-none mb-3 text-white">
                Try a Class Free
              </h3>
              <p className="text-white/60 text-sm mb-6">
                First bootcamp class on the house. No card required. Just show up and sweat.
              </p>
              <ul className="space-y-2 mb-6 text-sm text-white/85">
                <li className="flex gap-2"><span className="text-[#005EEA]">✓</span> Any weekly bootcamp class</li>
                <li className="flex gap-2"><span className="text-[#005EEA]">✓</span> One free class per person</li>
                <li className="flex gap-2"><span className="text-[#005EEA]">✓</span> Coach confirms your spot via email</li>
              </ul>
              <button
                onClick={tryFree}
                className="mt-auto w-full font-mono-accent text-xs uppercase tracking-[0.2em] bg-[#005EEA] text-white py-4 hover:bg-[#0047b3] transition-colors flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(0,94,234,0.4)]"
                data-testid="quickbuy-free-btn"
              >
                Claim Free Class <ArrowRight size={13} />
              </button>
            </article>
          </Reveal>

          {/* CARD 2 — Bootcamp drop-in */}
          {dropIn && (
            <Reveal delay={0.04}>
              <article
                className="relative border border-white/15 bg-[#080808] p-7 md:p-8 h-full flex flex-col hover:bg-[#0b0b0b] transition-colors"
                data-testid="quickbuy-dropin"
              >
                <Zap className="text-[#005EEA] mb-4" size={26} />
                <p className="font-mono-accent text-[10px] text-white/40 mb-2">{dropIn.tagline}</p>
                <h3 className="font-display text-2xl md:text-3xl leading-none mb-3 text-white">
                  Bootcamp Drop-In
                </h3>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="font-display text-5xl text-white leading-none">
                    {formatPrice(dropIn.terms[0].price_cents, dropIn.currency)}
                  </span>
                  <span className="text-white/40 text-xs font-mono-accent">/ CLASS</span>
                </div>
                <p className="text-white/60 text-sm mb-6">
                  Single class, no strings. Perfect if you can&apos;t commit monthly yet.
                </p>
                <ul className="space-y-2 mb-6 text-sm text-white/85">
                  {dropIn.features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <span className="text-[#005EEA]">✓</span> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => buyDirect(dropIn)}
                  disabled={loadingId === dropIn.id}
                  className="mt-auto w-full font-mono-accent text-xs uppercase tracking-[0.2em] bg-white text-black py-4 hover:bg-[#005EEA] hover:text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="quickbuy-dropin-btn"
                >
                  {loadingId === dropIn.id ? (
                    <><Loader2 size={13} className="animate-spin" /> Loading</>
                  ) : (
                    <>Book Drop-In <ArrowRight size={13} /></>
                  )}
                </button>
              </article>
            </Reveal>
          )}

          {/* CARD 3 — 3-Day PT trial */}
          {trial && (
            <Reveal delay={0.06}>
              <article
                className="relative border border-white/15 bg-[#0b0f1a] p-7 md:p-8 h-full flex flex-col shadow-[0_0_60px_rgba(0,94,234,0.18)]"
                data-testid="quickbuy-trial"
              >
                <span className="absolute top-0 right-0 bg-black text-white font-mono-accent text-[10px] px-4 py-2 border-l border-b border-white/15">
                  BEST STARTER
                </span>
                <Sparkles className="text-[#005EEA] mb-4" size={26} />
                <p className="font-mono-accent text-[10px] text-white/40 mb-2">{trial.tagline}</p>
                <h3 className="font-display text-2xl md:text-3xl leading-none mb-3 text-white">
                  3-Day PT Trial
                </h3>
                <div className="flex items-baseline gap-2 mb-3">
                  <span className="font-display text-5xl text-white leading-none">
                    {formatPrice(trial.terms[0].price_cents, trial.currency)}
                  </span>
                  <span className="text-white/40 text-xs font-mono-accent">/ 3 SESSIONS</span>
                </div>
                <p className="text-white/60 text-sm mb-6">
                  Assessment + 2 full 1-on-1 workouts. Test-drive personal training risk-free.
                </p>
                <ul className="space-y-2 mb-6 text-sm text-white/85">
                  {trial.features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <span className="text-[#005EEA]">✓</span> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => buyDirect(trial)}
                  disabled={loadingId === trial.id}
                  className="mt-auto w-full font-mono-accent text-xs uppercase tracking-[0.2em] bg-[#005EEA] text-white py-4 hover:bg-[#0047b3] transition-colors flex items-center justify-center gap-2 disabled:opacity-50 shadow-[0_0_30px_rgba(0,94,234,0.4)]"
                  data-testid="quickbuy-trial-btn"
                >
                  {loadingId === trial.id ? (
                    <><Loader2 size={13} className="animate-spin" /> Loading</>
                  ) : (
                    <>Start Your Trial <ArrowRight size={13} /></>
                  )}
                </button>
              </article>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
